<script lang="ts" setup>
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { faEnvelope, faLink, faShareNodes as faShareNodesRegular } from '@fortawesome/pro-regular-svg-icons';
import { faPrint, faShareNodes as faShareNodesSolid } from '@fortawesome/pro-solid-svg-icons';
import { faFacebookF, faLinkedinIn, faWhatsapp, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useI18n } from 'vue-i18n';
import { getLocation } from '@/utilities';
import { useTracking } from '@/utilities/tracking.ts';

const props = defineProps<{
  projectId: number;
  modelId: number;
}>();

const { trackAction } = useTracking();
const { t } = useI18n();

const location = getLocation();
const encodedUrl = encodeURIComponent(location.href);

function trackShare() {
  void trackAction(props.modelId > 0 ? 'projectModel' : 'project', props.modelId > 0 ? props.modelId : props.projectId, 'share');
}

function shareOnFacebook() {
  trackShare();
  window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, '_blank');
}

function shareOnLinkedIn() {
  trackShare();
  window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, '_blank');
}

function shareOnX() {
  trackShare();
  window.open(`https://twitter.com/intent/tweet?url=${encodedUrl}`, '_blank');
}

function shareOnWhatsapp() {
  trackShare();
  window.open(`https://wa.me/?text=${encodedUrl}`, '_blank');
}

function shareViaEmail() {
  trackShare();
  const subject = encodeURIComponent(document.title);
  const body = encodedUrl;
  window.location.href = `mailto:?subject=${subject}&body=${body}`;
}

async function copyLink() {
  await navigator.clipboard.writeText(window.location.href);
  trackShare();
}

function printPage() {
  void trackAction(props.modelId > 0 ? 'projectModel' : 'project', props.modelId > 0 ? props.modelId : props.projectId, 'print');
  window.print();
}
</script>

<template>
  <Menu v-slot="{ open }" as="div" class="no-print relative inline-block text-left">
    <div>
      <MenuButton class="group cursor-pointer rounded-full p-2">
        <FontAwesomeIcon :class="{ invisible: open }" :icon="faShareNodesRegular" class="size-6" size="2xl" />
        <FontAwesomeIcon :class="{ visible: open, 'invisible group-hover:visible': !open }" :icon="faShareNodesSolid" class="absolute top-2 left-2 size-6" size="2xl" />
        <span class="sr-only">{{ t('views.project.share.share') }}</span>
      </MenuButton>
    </div>
    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <MenuItems class="absolute right-0 z-20 mt-1 origin-top-right rounded-md bg-white text-nowrap shadow-lg ring-1 ring-black/5 focus:outline-hidden">
        <div class="py-1">
          <MenuItem v-slot="{ active }">
            <button :class="[active ? 'bg-gray-100 text-gray-900 outline-hidden' : 'text-gray-700', 'flex w-full cursor-pointer items-center py-2 pr-8 pl-4 text-left']" @click="shareOnFacebook">
              <FontAwesomeIcon :icon="faFacebookF" class="mr-2 !size-4" />
              {{ t('views.project.share.facebook') }}
            </button>
          </MenuItem>
          <MenuItem v-slot="{ active }">
            <button :class="[active ? 'bg-gray-100 text-gray-900 outline-hidden' : 'text-gray-700', 'flex w-full cursor-pointer items-center py-2 pr-8 pl-4 text-left']" @click="shareOnLinkedIn">
              <FontAwesomeIcon :icon="faLinkedinIn" class="mr-2 !size-4" />
              {{ t('views.project.share.linked-in') }}
            </button>
          </MenuItem>
          <MenuItem v-slot="{ active }">
            <button :class="[active ? 'bg-gray-100 text-gray-900 outline-hidden' : 'text-gray-700', 'flex w-full cursor-pointer items-center py-2 pr-8 pl-4 text-left']" @click="shareOnX">
              <FontAwesomeIcon :icon="faXTwitter" class="mr-2 !size-4" />
              {{ t('views.project.share.x') }}
            </button>
          </MenuItem>
          <MenuItem v-slot="{ active }">
            <button :class="[active ? 'bg-gray-100 text-gray-900 outline-hidden' : 'text-gray-700', 'flex w-full cursor-pointer items-center py-2 pr-8 pl-4 text-left']" @click="shareOnWhatsapp">
              <FontAwesomeIcon :icon="faWhatsapp" class="mr-2 !size-4" />
              {{ t('views.project.share.whatsapp') }}
            </button>
          </MenuItem>
          <MenuItem v-slot="{ active }">
            <button :class="[active ? 'bg-gray-100 text-gray-900 outline-hidden' : 'text-gray-700', 'flex w-full cursor-pointer items-center py-2 pr-8 pl-4 text-left']" @click="shareViaEmail">
              <FontAwesomeIcon :icon="faEnvelope" class="mr-2 !size-4" />
              {{ t('views.project.share.email') }}
            </button>
          </MenuItem>
          <MenuItem v-slot="{ active }">
            <button :class="[active ? 'bg-gray-100 text-gray-900 outline-hidden' : 'text-gray-700', 'flex w-full cursor-pointer items-center py-2 pr-8 pl-4 text-left']" @click="copyLink">
              <FontAwesomeIcon :icon="faLink" class="mr-2 !size-4" />
              {{ t('views.project.share.copy-link') }}
            </button>
          </MenuItem>
          <MenuItem v-slot="{ active }">
            <button :class="[active ? 'bg-gray-100 text-gray-900 outline-hidden' : 'text-gray-700', 'flex w-full cursor-pointer items-center py-2 pr-8 pl-4 text-left']" @click="printPage">
              <FontAwesomeIcon :icon="faPrint" class="mr-2 !size-4" />
              {{ t('views.project.share.print') }}
            </button>
          </MenuItem>
        </div>
      </MenuItems>
    </transition>
  </Menu>
</template>

<style lang="css" scoped></style>
